import React, { useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EmployeeDirectory = ({ hrProfiles, loading, onRefresh, gymId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success/10 text-success';
      case 'on_leave':
        return 'bg-warning/10 text-warning';
      case 'terminated':
        return 'bg-error/10 text-error';
      case 'suspended':
        return 'bg-error/10 text-error';
      case 'probation':
        return 'bg-warning/10 text-warning';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const filteredProfiles = hrProfiles?.filter((profile) => {
    const matchesSearch =
      profile?.staff?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      profile?.employee_number?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      profile?.position?.toLowerCase()?.includes(searchTerm?.toLowerCase());

    const matchesStatus = filterStatus === 'all' || profile?.employment_status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return `$${parseFloat(amount)?.toFixed(2)}`;
  };

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Icon name="Search" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e?.target?.value)}
            className="px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="on_leave">On Leave</option>
            <option value="probation">Probation</option>
            <option value="suspended">Suspended</option>
            <option value="terminated">Terminated</option>
          </select>

          <Button onClick={onRefresh} variant="outline" disabled={loading}>
            <Icon name={loading ? 'Loader2' : 'RefreshCw'} size={18} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>
      {/* Employee Grid */}
      {loading && hrProfiles?.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      ) : filteredProfiles?.length === 0 ? (
        <div className="card text-center py-12">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Employees Found</h3>
          <p className="text-muted-foreground">Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProfiles?.map((profile) => (
            <div
              key={profile?.id}
              className="card hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => setSelectedEmployee(profile)}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon name="User" size={32} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-foreground truncate">
                    {profile?.staff?.full_name}
                  </h3>
                  <p className="text-sm text-muted-foreground truncate">{profile?.position}</p>
                  <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(profile?.employment_status)}`}>
                    {profile?.employment_status?.replace('_', ' ')}
                  </span>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Employee #:</span>
                  <span className="font-medium text-foreground">{profile?.employee_number}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Department:</span>
                  <span className="font-medium text-foreground">{profile?.department || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Hire Date:</span>
                  <span className="font-medium text-foreground">
                    {profile?.hire_date ? format(new Date(profile.hire_date), 'MMM d, yyyy') : 'N/A'}
                  </span>
                </div>
                {profile?.hourly_rate && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Hourly Rate:</span>
                    <span className="font-medium text-primary">{formatCurrency(profile?.hourly_rate)}/hr</span>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-xs">
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Icon name="Calendar" size={12} />
                  <span>PTO: {profile?.pto_balance_hours || 0}h</span>
                </div>
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Icon name="Heart" size={12} />
                  <span>Sick: {profile?.sick_leave_balance_hours || 0}h</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Employee Detail Modal */}
      {selectedEmployee && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedEmployee(null)}>
          <div className="card max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e?.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-1">
                  {selectedEmployee?.staff?.full_name}
                </h2>
                <p className="text-muted-foreground">{selectedEmployee?.position}</p>
              </div>
              <button
                onClick={() => setSelectedEmployee(null)}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="space-y-6">
              {/* Employment Info */}
              <div>
                <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="Briefcase" size={18} className="text-primary" />
                  Employment Information
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-muted-foreground mb-1">Employee Number</div>
                    <div className="font-medium text-foreground">{selectedEmployee?.employee_number}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-1">Department</div>
                    <div className="font-medium text-foreground">{selectedEmployee?.department || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-1">Hire Date</div>
                    <div className="font-medium text-foreground">
                      {selectedEmployee?.hire_date ? format(new Date(selectedEmployee.hire_date), 'MMM d, yyyy') : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-1">Status</div>
                    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(selectedEmployee?.employment_status)}`}>
                      {selectedEmployee?.employment_status?.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Compensation */}
              <div>
                <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="DollarSign" size={18} className="text-primary" />
                  Compensation
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {selectedEmployee?.hourly_rate && (
                    <div>
                      <div className="text-muted-foreground mb-1">Hourly Rate</div>
                      <div className="font-medium text-foreground">{formatCurrency(selectedEmployee?.hourly_rate)}/hr</div>
                    </div>
                  )}
                  {selectedEmployee?.salary && (
                    <div>
                      <div className="text-muted-foreground mb-1">Annual Salary</div>
                      <div className="font-medium text-foreground">{formatCurrency(selectedEmployee?.salary)}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-muted-foreground mb-1">Pay Frequency</div>
                    <div className="font-medium text-foreground capitalize">{selectedEmployee?.pay_frequency || 'N/A'}</div>
                  </div>
                </div>
              </div>

              {/* Time Off Balances */}
              <div>
                <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="Calendar" size={18} className="text-primary" />
                  Time Off Balances
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-muted-foreground mb-1">PTO Balance</div>
                    <div className="font-medium text-foreground">{selectedEmployee?.pto_balance_hours || 0} hours</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-1">Sick Leave Balance</div>
                    <div className="font-medium text-foreground">{selectedEmployee?.sick_leave_balance_hours || 0} hours</div>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div>
                <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="Phone" size={18} className="text-primary" />
                  Contact Information
                </h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <div className="text-muted-foreground mb-1">Email</div>
                    <div className="font-medium text-foreground">{selectedEmployee?.staff?.email}</div>
                  </div>
                  {selectedEmployee?.emergency_contact_name && (
                    <div>
                      <div className="text-muted-foreground mb-1">Emergency Contact</div>
                      <div className="font-medium text-foreground">
                        {selectedEmployee?.emergency_contact_name} ({selectedEmployee?.emergency_contact_relationship})
                      </div>
                      <div className="text-foreground">{selectedEmployee?.emergency_contact_phone}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeDirectory;